import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Droplets, Plus, Settings, Trash2, X } from 'lucide-react';
import {
  addWater,
  getDailyGoal,
  getTodayLog,
  getWeekHistory,
  removeWater,
  setDailyGoal
} from '../../services/waterPlugin';

const QUICK_AMOUNTS = [100, 200, 250, 330, 500, 750];
const GOAL_PRESETS = [1500, 2000, 2500, 3000, 3500, 4000];

function formatAmount(amount) {
  return amount >= 1000 ? `${(amount / 1000).toFixed(1)} L` : `${amount} ml`;
}

function formatTime(value) {
  return new Date(value).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });
}

function ArcProgress({ totalMl, goalMl, size = 200 }) {
  const strokeWidth = 15;
  const radius = (size - strokeWidth * 2) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * 0.75;
  const percentage = goalMl > 0 ? Math.min(1, totalMl / goalMl) : 0;
  const color = percentage >= 1 ? '#10B981' : percentage >= 0.6 ? '#0077FF' : '#F59E0B';
  const rotation = `rotate(135 ${center} ${center})`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${Math.round(percentage * 100)}% of daily water goal`}
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="#EAF4FF"
        strokeWidth={strokeWidth}
        strokeDasharray={`${arcLength} ${circumference}`}
        strokeLinecap="round"
        transform={rotation}
      />
      {percentage > 0 && (
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={`${arcLength * percentage} ${circumference}`}
          strokeLinecap="round"
          transform={rotation}
          style={{ transition: 'stroke-dasharray 0.4s ease' }}
        />
      )}
    </svg>
  );
}

export default function WaterLog() {
  const [log, setLog] = useState({ entries: [], goal: 2500 });
  const [week, setWeek] = useState([]);
  const [customAmount, setCustomAmount] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [goalInput, setGoalInput] = useState('2500');

  const reload = useCallback(() => {
    setLog(getTodayLog());
    setWeek(getWeekHistory());
    setGoalInput(String(getDailyGoal()));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const total = log.entries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  const goal = log.goal || 2500;
  const percentage = goal > 0 ? Math.min(100, Math.round((total / goal) * 100)) : 0;
  const remaining = Math.max(0, goal - total);
  const weekMax = Math.max(1, goal, ...week.map((day) => day.total));

  const handleAdd = (amount) => {
    setLog(addWater(amount));
    setWeek(getWeekHistory());
  };

  const handleRemove = (index) => {
    setLog(removeWater(index));
    setWeek(getWeekHistory());
  };

  const handleCustomAdd = () => {
    const amount = Number.parseInt(customAmount, 10);
    if (amount >= 1 && amount <= 5000) {
      handleAdd(amount);
      setCustomAmount('');
      setShowCustom(false);
    }
  };

  const handleSaveGoal = () => {
    const nextGoal = Number.parseInt(goalInput, 10);
    if (nextGoal >= 500 && nextGoal <= 10000) {
      setDailyGoal(nextGoal);
      reload();
      setShowSettings(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-lg px-3 pb-24 pt-1 sm:px-4"
    >
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="m3-label">Health tracker</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950">Water log</h1>
        </div>
        <button
          type="button"
          onClick={() => setShowSettings(true)}
          className="m3-icon-button"
          aria-label="Water goal settings"
        >
          <Settings size={19} />
        </button>
      </div>

      <section className="m3-card mb-5 flex flex-col items-center p-5">
        <div className="relative">
          <ArcProgress totalMl={total} goalMl={goal} />
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <Droplets size={20} className="mb-1 text-blue-500" />
            <span className="text-4xl font-black leading-none text-slate-950">
              {total >= 1000 ? (total / 1000).toFixed(1) : total}
            </span>
            <span className="mt-1 text-sm font-bold text-slate-500">{total >= 1000 ? 'L' : 'ml'}</span>
            <span className="mt-1 text-xs font-bold text-slate-500">of {formatAmount(goal)}</span>
          </div>
        </div>
        <div className="mt-3 grid w-full grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-black text-slate-950">{percentage}%</p>
            <p className="m3-label">Complete</p>
          </div>
          <div>
            <p className={`text-lg font-black ${percentage >= 100 ? 'text-emerald-600' : 'text-blue-600'}`}>
              {percentage >= 100 ? 'Goal met' : 'In progress'}
            </p>
            <p className="m3-label">Today</p>
          </div>
          <div>
            <p className="text-lg font-black text-slate-950">{formatAmount(remaining)}</p>
            <p className="m3-label">Remaining</p>
          </div>
        </div>
      </section>

      <section className="mb-5">
        <h2 className="m3-label mb-3">Quick add</h2>
        <div className="mb-2 grid grid-cols-3 gap-2">
          {QUICK_AMOUNTS.map((amount) => (
            <button
              key={amount}
              type="button"
              onClick={() => handleAdd(amount)}
              className="touch-target rounded-2xl border border-slate-200 bg-white text-sm font-extrabold text-slate-800 transition hover:border-blue-400 hover:bg-blue-50 active:scale-95"
            >
              +{formatAmount(amount)}
            </button>
          ))}
        </div>
        {showCustom ? (
          <div className="flex gap-2">
            <input
              type="number"
              min="1"
              max="5000"
              inputMode="numeric"
              value={customAmount}
              onChange={(event) => setCustomAmount(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleCustomAdd()}
              placeholder="Amount in ml"
              aria-label="Custom water amount in milliliters"
              className="h-12 min-w-0 flex-1 rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-blue-400"
              autoFocus
            />
            <button
              type="button"
              onClick={handleCustomAdd}
              className="touch-target rounded-2xl bg-blue-600 px-4 text-sm font-black text-white hover:bg-blue-700"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => { setShowCustom(false); setCustomAmount(''); }}
              className="m3-icon-button border border-slate-200"
              aria-label="Cancel custom amount"
            >
              <X size={17} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowCustom(true)}
            className="touch-target flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 text-sm font-extrabold text-slate-600 transition hover:border-blue-400 hover:text-blue-600"
          >
            <Plus size={17} /> Custom amount
          </button>
        )}
      </section>

      <section className="m3-card mb-5 p-4">
        <h2 className="m3-label mb-4">This week</h2>
        <div className="flex h-24 items-end gap-2" aria-label="Water intake for the past seven days">
          {week.map((day) => {
            const barHeight = Math.max(4, Math.round((day.total / weekMax) * 72));
            const metGoal = day.total >= day.goal;
            return (
              <div key={day.key} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-2">
                <div
                  className={`w-full rounded-lg transition-all ${metGoal ? 'bg-blue-500' : 'bg-blue-100'}`}
                  style={{ height: barHeight }}
                  title={`${day.label}: ${formatAmount(day.total)} / ${formatAmount(day.goal)}`}
                />
                <span className="text-[9px] font-bold text-slate-500">{day.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="m3-label mb-3">
          Today · {log.entries.length} {log.entries.length === 1 ? 'entry' : 'entries'}
        </h2>
        {log.entries.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-slate-400">
            <Droplets size={34} />
            <p className="mt-2 text-sm font-bold">No water logged yet today</p>
            <p className="mt-1 text-xs font-medium">Tap a quick add button to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {[...log.entries].reverse().map((entry, reverseIndex) => {
              const index = log.entries.length - 1 - reverseIndex;
              return (
                <div key={`${entry.time}-${index}`} className="m3-card flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-2xl bg-blue-50 text-blue-600">
                      <Droplets size={17} />
                    </span>
                    <div>
                      <p className="text-sm font-extrabold text-slate-950">{formatAmount(entry.amount)}</p>
                      <p className="text-xs font-medium text-slate-500">{formatTime(entry.time)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="m3-icon-button h-10 w-10 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                    aria-label={`Remove ${formatAmount(entry.amount)} entry`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {showSettings && (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/35 px-3 pb-3 backdrop-blur-[2px] md:items-center"
          onMouseDown={(event) => event.target === event.currentTarget && setShowSettings(false)}
        >
          <motion.section
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="m3-sheet w-full max-w-sm rounded-[28px] bg-white p-5"
            role="dialog"
            aria-modal="true"
            aria-labelledby="water-goal-title"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 id="water-goal-title" className="text-xl font-black text-slate-950">Daily water goal</h2>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="m3-icon-button h-10 w-10"
                aria-label="Close goal settings"
              >
                <X size={18} />
              </button>
            </div>
            <div className="mb-4 grid grid-cols-3 gap-2">
              {GOAL_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setGoalInput(String(preset))}
                  className={`touch-target rounded-2xl text-sm font-extrabold transition ${
                    goalInput === String(preset) ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-blue-50'
                  }`}
                >
                  {formatAmount(preset)}
                </button>
              ))}
            </div>
            <label htmlFor="water-goal-input" className="m3-label mb-2 block">Custom goal (500–10,000 ml)</label>
            <input
              id="water-goal-input"
              type="number"
              min="500"
              max="10000"
              step="100"
              inputMode="numeric"
              value={goalInput}
              onChange={(event) => setGoalInput(event.target.value)}
              className="mb-4 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-blue-400"
            />
            <button
              type="button"
              onClick={handleSaveGoal}
              disabled={Number(goalInput) < 500 || Number(goalInput) > 10000}
              className="touch-target w-full rounded-2xl bg-blue-600 text-sm font-black text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save goal
            </button>
          </motion.section>
        </div>
      )}
    </motion.div>
  );
}
